/*
 * Kit configurator — sections/kit-configurator-custom.liquid
 *
 * State = { nearside: unitKey|null, offside: unitKey|null, mats: { nearside, offside },
 *           accessories: { variantId: qty } } — accessories are the optional step 3,
 * rendered from a curated collection with the theme's stock quantity-input per card;
 * the send-your-build button opens WhatsApp or email with the build written out
 * (no backend, the customer's own app sends it).
 * The section renders the option rows and gallery server-side; this fills the per-unit
 * spec detail from the unit JSON, paints the plan view, keeps the summary/total in step,
 * mirrors the selection into the URL (?ns=&os=&mats=) and adds every selected item to the
 * cart as its own line item through the same drawer flow product-form.js uses.
 *
 * Unit records come from <script data-kc-units>, built from real variants; each record's
 * spec is the variant's custom.unit metafield. A unit without one still renders and is
 * buyable — it just has no spec detail and no mat-pack tick.
 */
if (!customElements.get('kit-configurator')) {
  customElements.define(
    'kit-configurator',
    class KitConfigurator extends HTMLElement {
      constructor() {
        super();
        this.state = { nearside: null, offside: null, mats: { nearside: false, offside: false }, accessories: {} };
        this.emptyChosen = { nearside: false, offside: false };
        this.units = {};
        this.sides = ['nearside', 'offside'];
        this.sideParam = { nearside: 'ns', offside: 'os' };
        this.vatMultiplier = parseFloat(this.dataset.vatMultiplier) || 1.2;
        this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
      }

      connectedCallback() {
        this.applyVanFromLink();
        this.readUnits();
        this.hydrateOptions();
        this.alignReservedMatRows();
        this.bind();
        this.applyDeepLink();
        this.initVanPicker();
        this.initVizCollapse();
        this.initDetailAnimation();
        this.initLightbox();
        this.initZoom();
        this.render();
      }

      /* ── Which van is this build for ──────────────────────────────── */

      /* A shared kit fits many vans - SK036 fits 26 - so the page product alone cannot say
         which one the customer is buying for. The server renders fits_vans | first, which is
         the wrong vehicle on 84% of van/kit pairs and frequently the wrong make: every
         "Ford Transit L2H2" link on the Transit collection opened "Build your kit - Citroen
         Relay" (David, 24 Aug 2026).

         The links now carry ?van=<handle>. Liquid cannot read a query parameter, so the match
         happens here, before anything else in connectedCallback: the heading, the van line and
         every card's Fits row are rewritten to the van actually asked for. An unknown or absent
         handle changes nothing, so a direct visit still renders exactly as the server sent it.

         The heading is only rewritten when the server says it came from the van - a heading
         typed into the theme editor is the merchant's words and stays put. */
      applyVanFromLink() {
        const wanted = new URLSearchParams(window.location.search).get('van');
        if (!wanted) return;
        // The select is the visible record of which van this build is for, so it follows the
        // link even when the link agrees with what the server rendered.
        const select = this.querySelector('[data-kc-van-select]');
        if (select && select.value !== wanted && this.vanByHandle(wanted)) select.value = wanted;
        if (wanted === this.dataset.vanHandle) return;
        this.applyVan(wanted);
      }

      /* The picker for everyone the deep link does not cover: the "Customise this kit"
         call-out on a kit page, and anyone landing on ?view=configurator directly. Neither
         carries a van, and both used to be answered with fits_vans | first.

         Changing the van rewrites the same things the link does and nothing else - no units
         are reselected, because a unit that fits one van in this kit's fitment fits them all;
         that is what makes them one kit. The URL is kept in step so a reload, a share or the
         Back button all describe the build the customer is actually looking at. */
      initVanPicker() {
        const select = this.querySelector('[data-kc-van-select]');
        if (!select) return;
        select.addEventListener('change', () => {
          if (!this.applyVan(select.value)) return;
          const url = new URL(window.location.href);
          url.searchParams.set('van', select.value);
          window.history.replaceState({}, '', url.toString());
        });
      }

      vanByHandle(handle) {
        if (!this.vans) {
          const node = this.querySelector('[data-kc-vans]');
          this.vans = [];
          if (node) { try { this.vans = JSON.parse(node.textContent) || []; } catch (e) { this.vans = []; } }
        }
        return this.vans.find((v) => v && v.handle === handle && v.name) || null;
      }

      /* Rewrites the heading, the van line and every card's Fits row to the named van, and
         updates dataset.van, which is what reaches the order on the _van cart property.
         An unknown handle changes nothing and reports false, so a hand-typed ?van= leaves
         the page exactly as the server sent it.

         The heading is only rewritten when the server says it came from the van - a heading
         typed into the theme editor is the merchant's words and stays put. */
      applyVan(handle) {
        const van = this.vanByHandle(handle);
        if (!van) return false;

        this.dataset.van = van.name;
        this.dataset.vanHandle = van.handle;

        if (this.dataset.headingFromVan === 'true') {
          const h = this.querySelector('[data-kc-heading]');
          if (h) h.textContent = 'Build your kit - ' + van.name;
        }
        const nameEl = this.querySelector('[data-kc-van-name]');
        if (nameEl) nameEl.textContent = van.name;
        const bodyEl = this.querySelector('[data-kc-van-body]');
        if (bodyEl) bodyEl.textContent = van.body ? ' · ' + van.body : '';
        this.querySelectorAll('[data-kc-fits]').forEach((dd) => {
          dd.textContent = van.name + ', ' + (dd.dataset.position || '');
        });
        return true;
      }

      /* ── Magnifier on the card image ──────────────────────────────── */

      /* The card used to carry a strip of thumbnails inside More info. It now carries a
         magnifier over the image instead (David, 24 Aug 2026), opening the viewer that was
         already there - swipe, arrow keys, Back button to close. The photos stay in the card
         as a hidden list of data-only elements, which is what gets handed over.

         stopPropagation matters: the magnifier sits inside the card-head label. A button is
         interactive content so the label should not forward the click to the radio, but the
         card also has its own click handling, and viewing photos must not double as picking
         the unit. */
      initZoom() {
        this.addEventListener('click', (evt) => {
          const btn = evt.target.closest('[data-kc-zoom]');
          if (!btn || !this.contains(btn)) return;
          evt.preventDefault();
          evt.stopPropagation();
          const card = btn.closest('[data-kc-unit], [data-kc-acc]');
          const strip = card && card.querySelector('[data-kc-gallery]');
          if (!strip) return;
          const items = Array.from(strip.querySelectorAll('[data-kc-gallery-item]'))
            .map((el) => ({ full: el.dataset.full, alt: el.dataset.alt || '' }))
            .filter((it) => it.full);
          if (items.length) this.openLightbox(items, 0, btn);
        });
      }

      /* ── More info: grow and shrink instead of snapping ───────────── */

      /* The body used to appear in one frame, adding about 450px instantly. Nothing above it
         moved - measured - but an instant jump of that size still reads as the page jumping,
         which is what David reported on 24 Aug 2026. The reference he gave
         (kidsrideshotgun.co.uk product cards) animates its accordion open over 0.3s, so it
         reads as expanding rather than teleporting.

         Animating a <details> is not a plain CSS job: the browser hides the body outright
         when it is closed, so there is nothing to transition from. Height is driven here
         instead, from 0 to the body's real scrollHeight, which also avoids the usual
         max-height guess - a cap too low silently clips the content and a cap too high makes
         the easing finish early. The element stays a real <details>, so it still opens
         without JS and keyboard and screen readers are unaffected. */
      initDetailAnimation() {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.querySelectorAll('details.kit-configurator__detail').forEach((det) => {
          const body = det.querySelector('.kit-configurator__detail-body');
          const summary = det.querySelector('summary');
          if (!body || !summary) return;

          summary.addEventListener('click', (event) => {
            // Respect the OS setting: no animation, let <details> do its normal thing.
            if (reduced.matches) return;
            event.preventDefault();
            if (det.dataset.kcAnimating === '1') return;
            det.dataset.kcAnimating = '1';

            const done = () => {
              body.style.height = '';
              body.style.overflow = '';
              det.dataset.kcAnimating = '0';
            };
            const onEnd = (fn) => {
              const handler = (e) => {
                if (e.target !== body || e.propertyName !== 'height') return;
                body.removeEventListener('transitionend', handler);
                fn();
              };
              body.addEventListener('transitionend', handler);
              // A transition that never fires (display quirks, zero duration) must not leave
              // the card stuck half open.
              setTimeout(() => { if (det.dataset.kcAnimating === '1') { body.removeEventListener('transitionend', handler); fn(); } }, 600);
            };

            body.style.overflow = 'hidden';

            // Both heights have to land in separate style recalculations or there is no start
            // value to animate from. requestAnimationFrame is not enough - its callbacks run
            // before layout, so the two writes collapse into one frame and the transition is
            // skipped. Reading offsetHeight in between forces the first one to take effect.
            // (That is exactly how the close came to snap shut while the open animated.)
            if (det.open) {
              body.style.height = body.scrollHeight + 'px';
              void body.offsetHeight;
              body.style.height = '0px';
              onEnd(() => { det.open = false; done(); });
            } else {
              det.open = true;
              const full = body.scrollHeight;
              body.style.height = '0px';
              void body.offsetHeight;
              body.style.height = full + 'px';
              onEnd(done);
            }
          });
        });
      }

      /* ── Data ─────────────────────────────────────────────────────── */

      readUnits() {
        const unitsEl = this.querySelector('script[data-kc-units]');
        let list = [];
        try {
          list = JSON.parse(unitsEl ? unitsEl.textContent : '[]');
        } catch (e) {
          console.error('kit-configurator: unit JSON did not parse', e);
        }
        list.forEach((unit) => {
          this.units[unit.key] = unit;
        });
      }

      unitsFor(side) {
        return Object.values(this.units).filter((u) => u.position === side);
      }

      /* ── Money ────────────────────────────────────────────────────── */

      formatMoney(cents) {
        const value = (cents / 100).toFixed(2);
        const [whole, dec] = value.split('.');
        const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
      }

      // Every price in custom.unit, and every variant price on this store, is the NET
      // figure: shop.taxesIncluded is false, so checkout adds the VAT on top. The stored
      // number IS the ex-VAT one and inc VAT is derived by multiplying. Until 30 Aug 2026
      // this divided instead, which quoted every build 16.7% under what checkout charged.
      incVat(cents) {
        return Math.round(cents * this.vatMultiplier);
      }

      /* Mat pack prices are EX VAT, the same basis as every other price on this store, so they
         need no conversion here. They were not always: payloads written before 1 Sep 2026 held
         spec.mat_pack.price INC VAT, and this file carried a normaliseMatPrice() that converted
         whenever the payload lacked a `price_basis` marker. Every payload on the store now
         carries it (checked 2 Sep 2026: 144 of 144), so the branch was dead and is gone.
         data/lib/render.js still STAMPS the marker, deliberately. Do not remove it there until
         a theme without this branch is published, or an apply landing first would put every
         mat price 16.7% under on the live storefront. */

      /* ── Fill each option's spec detail from the unit JSON ────────── */

      hydrateOptions() {
        this.querySelectorAll('[data-kc-unit]').forEach((opt) => {
          const unit = this.units[opt.dataset.kcUnit];
          if (!unit) return;
          const spec = unit.spec;
          const specList = opt.querySelector('[data-kc-spec]');
          const contents = opt.querySelector('[data-kc-contents]');
          const links = opt.querySelector('[data-kc-links]');

          // Mat pack tick-box: on EVERY card, not just the chosen one (David, 24 Aug 2026), so
          // the option and its price are part of comparing units. Priced from THIS card's own
          // pack.
          //
          // A unit with no pack - the four worktop-module nearsides WK034/036/038/040 - still
          // RESERVES the row rather than dropping it (David, 24 Aug 2026, from a phone shot of
          // kit 038 where WK038_NS sat visibly short beside its neighbour). Without it the card
          // is a row shorter, so its border and its Select bar stop lining up with the rest.
          // The placeholder keeps the same markup, so it reserves exactly the height the real
          // row would take; CSS makes it invisible, which also takes the checkbox out of the
          // tab order and the accessibility tree. Disabling the input as well means a missing
          // stylesheet cannot leave a tickable row that does nothing.
          //
          // This runs BEFORE the no-spec return below, so a unit with no custom.unit at all
          // reserves the space too instead of being the odd short card.
          const matEl = opt.querySelector('[data-kc-mat]');
          if (matEl) {
            const pack = spec && spec.mat_pack;
            if (pack) {
              // pack.price is ex-VAT cents, like every price in custom.unit. The row renders it
              // ex VAT (25 Aug 2026) so the +£ delta is in the same basis as the unit price above
              // it and the total below it; before this it was inc VAT with no label at all.
              const matPrice = matEl.querySelector('[data-kc-mat-price]');
              if (matPrice) matPrice.textContent = this.formatMoney(pack.price || 0);
            } else {
              matEl.classList.add('kit-configurator__mat--placeholder');
              const matInput = matEl.querySelector('[data-kc-mat-input]');
              if (matInput) matInput.disabled = true;
            }
            matEl.hidden = false;
          }

          if (!spec) return;

          if (specList) {
            const rows = [];
            if (spec.dimensions_mm) {
              // dimensions_mm is the OVERALL run size (w spans every frame), so no
              // "N ×" prefix from the frame count - that read as N separate units.
              // Height first (David, 25 Aug 2026). It also matches kit-accordion-custom on the
              // kit pages, which has always printed H x W x D - this was the odd one out.
              const d = spec.dimensions_mm;
              // An L-shaped unit leads with its floor footprint and breaks it down underneath,
              // because the run length alone understates how much wall it needs: the bulkhead
              // bench's corner takes up part of it (David, 3 Sep 2026). Same wording as the
              // product page's dimensions line, so the two surfaces cannot disagree.
              if (spec.footprint_mm) {
                const f = spec.footprint_mm;
                rows.push(['Size', `${d.h} H × ${f.w} W × ${f.d} D mm overall`]);
                const b = spec.bulkhead_mm;
                rows.push(['Made up of', `a ${d.w} W × ${d.d} D run${b ? ` and a ${b.w} W × ${b.d} D bulkhead bench` : ''}`]);
              } else {
                rows.push(['Size', `${d.h} H × ${d.w} W × ${d.d} D mm`]);
              }
            }
            if (spec.load_rating) {
              const lr = spec.load_rating;
              const parts = [];
              if (lr.per_shelf_kg) parts.push(`${lr.per_shelf_kg} kg per shelf`);
              if (lr.per_drawer_kg) parts.push(`${lr.per_drawer_kg} kg per drawer`);
              if (parts.length) rows.push(['Load rating', parts.join(' · ')]);
            }
            if (spec.unit_weight_kg) rows.push(['Weight', `${spec.unit_weight_kg} kg`]);
            rows.forEach(([dt, dd]) => {
              const dtEl = document.createElement('dt');
              dtEl.textContent = dt;
              const ddEl = document.createElement('dd');
              ddEl.textContent = dd;
              specList.append(dtEl, ddEl);
            });
          }

          if (contents && Array.isArray(spec.contents)) {
            const merged = this.mergedContents(spec);
            merged.forEach((item) => {
              const li = document.createElement('li');
              li.textContent = item.label || (item.qty > 1 ? `${item.qty} × ${item.part}` : item.part);
              contents.appendChild(li);
            });
            const count = opt.querySelector('[data-kc-contents-count]');
            if (count) count.textContent = merged.length ? String(merged.length) : '';
          }

          // Downloads live in their own dropdown now (David, 24 Aug 2026), and that dropdown
          // ships hidden: most units have no drawings yet, and an empty panel is worse than no
          // panel. It is revealed here only once there is something to put in it. The row also
          // starts empty since the "Full product page" link went, so downloads simply append.
          if (links && Array.isArray(spec.downloads) && spec.downloads.length) {
            const panel = opt.querySelector('[data-kc-downloads]');
            if (panel) panel.hidden = false;
            const dlCount = opt.querySelector('[data-kc-links-count]');
            if (dlCount) dlCount.textContent = String(spec.downloads.length);
            spec.downloads.forEach((dl) => {
              const label = `${dl.label}${dl.type ? ` (${dl.type.toUpperCase()})` : ''}`;
              let el;
              if (dl.url) {
                el = document.createElement('a');
                el.className = 'link';
                el.href = dl.url;
                el.target = '_blank';
                el.rel = 'noopener';
              } else {
                el = document.createElement('span');
                el.className = 'kit-configurator__link--pending';
                el.title = 'Not uploaded yet';
              }
              el.textContent = label;
              links.appendChild(el);
            });
          }
        });
      }

      /* Second pass over the reserved mat rows. Giving the placeholder the SAME price text as
         a real row on that side is the difference between reserving roughly the right space
         and reserving exactly the right space: the row wraps to two lines once the label and
         the price no longer fit side by side, which happens on a narrow desktop card but not
         on a phone. An empty placeholder never wraps, so it came out 23px short at 1400px
         wide while looking perfect at 414. Identical content wraps identically at every width,
         with no resize handling to keep in step. The text is never seen - the row is
         visibility: hidden. */
      alignReservedMatRows() {
        this.querySelectorAll('[data-kc-side]').forEach((step) => {
          const rows = [...step.querySelectorAll('[data-kc-mat]')];
          const donor = rows.find((r) => !r.classList.contains('kit-configurator__mat--placeholder'));
          if (!donor) return; // every unit on this side lacks a pack, so they already match
          const donorPrice = donor.querySelector('[data-kc-mat-price]');
          if (!donorPrice) return;
          rows.forEach((r) => {
            if (!r.classList.contains('kit-configurator__mat--placeholder')) return;
            const p = r.querySelector('[data-kc-mat-price]');
            if (p) p.textContent = donorPrice.textContent;
          });
        });
      }

      /* Contents are stored per frame (and per shelf size), so one part can
         appear several times. Nothing in the configurator shows frames or
         sizes, so everything renders from this merged view: quantities summed
         per part name, first-appearance order. */
      mergedContents(spec) {
        const totals = new Map();
        const labels = new Map();
        (spec.contents || []).forEach((item) => {
          const part = String(item.part || '');
          if (!part) return;
          totals.set(part, (totals.get(part) || 0) + (item.qty || 1));
          // A part carrying `label` prints that text with no count, matching the product page's
          // features block: "Perforated End Frame/s", never "2 × Perforated End Frame" (3 Sep
          // 2026). The label comes from parts.csv no_qty_label via the custom.unit payload, so
          // the two surfaces cannot drift.
          if (item.label) labels.set(part, item.label);
        });
        return [...totals].map(([part, qty]) => ({ part, qty, label: labels.get(part) || null }));
      }

      /* ── Events ───────────────────────────────────────────────────── */

      bind() {
        this.querySelectorAll('[data-kc-input]').forEach((input) => {
          input.addEventListener('change', () => {
            const opt = input.closest('[data-kc-unit]');
            const unit = this.units[opt.dataset.kcUnit];
            if (!unit) return;
            // A different unit means a different mat pack, so switching clears the tick.
            if (this.state[unit.position] !== unit.key) this.state.mats[unit.position] = false;
            this.state[unit.position] = unit.key;
            this.emptyChosen[unit.position] = false;
            this.render();
          });
        });

        this.querySelectorAll('[data-kc-mat]').forEach((mat) => {
          const input = mat.querySelector('[data-kc-mat-input]');
          input.addEventListener('change', () => {
            const side = mat.dataset.kcMat;
            const unitKey = mat.dataset.kcMatUnit;
            // Every card carries a tick-box now, so one can be ticked on a unit that is not the
            // chosen one. That picks the unit as well: a mat pack with no unit under it means
            // nothing, and it saves a click for "that one, with mats".
            if (input.checked && this.state[side] !== unitKey && this.units[unitKey]) {
              this.state[side] = unitKey;
              this.emptyChosen[side] = false;
            }
            this.state.mats[side] = input.checked;
            this.render();
          });
        });

        // Accessories: Add puts the first one in; after that the stock quantity-input
        // (global.js) owns the count and a change to 0 removes it. Clicking the card head
        // also adds the first one, matching the unit cards' click-anywhere feel.
        const setAccQty = (card, qty) => {
          if (!card) return;
          if (qty > 0) this.state.accessories[card.dataset.kcAcc] = Math.min(qty, 99);
          else delete this.state.accessories[card.dataset.kcAcc];
          this.render();
        };
        this.querySelectorAll('[data-kc-acc-add]').forEach((btn) => {
          btn.addEventListener('click', () => setAccQty(btn.closest('[data-kc-acc]'), 1));
        });
        this.querySelectorAll('[data-kc-acc-head]').forEach((head) => {
          head.addEventListener('click', () => {
            const card = head.closest('[data-kc-acc]');
            if (card && !this.state.accessories[card.dataset.kcAcc]) setAccQty(card, 1);
          });
        });
        this.querySelectorAll('[data-kc-acc-input]').forEach((input) => {
          input.addEventListener('change', () => {
            const qty = parseInt(input.value, 10);
            setAccQty(input.closest('[data-kc-acc]'), isNaN(qty) || qty < 0 ? 0 : qty);
          });
        });

        const add = this.querySelector('[data-kc-add]');
        if (add) add.addEventListener('click', this.addToCart.bind(this));

        const share = this.querySelector('[data-kc-share]');
        if (share) share.addEventListener('click', this.shareBuild.bind(this));

        const quote = this.querySelector('[data-kc-quote]');
        if (quote) quote.addEventListener('click', this.quoteBuild.bind(this));

        // 'Leave this side empty' — an explicit un-pick, so one-sided kits are a choice, not an omission.
        this.querySelectorAll('[data-kc-empty]').forEach((row) => {
          const input = row.querySelector('[data-kc-empty-input]');
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const side = row.dataset.kcEmpty;
            this.state[side] = null;
            this.state.mats[side] = false;
            this.emptyChosen[side] = true;
            this.render();
          });
        });
      }

      applyDeepLink() {
        const params = new URLSearchParams(window.location.search);
        this.sides.forEach((side) => {
          const key = (params.get(this.sideParam[side]) || '').toLowerCase();
          if (key && this.units[key] && this.units[key].position === side) {
            this.state[side] = key;
            const input = this.querySelector(`[data-kc-unit="${CSS.escape(key)}"] [data-kc-input]`);
            if (input) {
              input.checked = true;
              // On phones the cards are a horizontal swipe strip — bring the pre-selected
              // card into view without scrolling the page itself.
              const card = input.closest('[data-kc-unit]');
              const strip = card && card.closest('.kit-configurator__cards');
              if (strip && strip.scrollWidth > strip.clientWidth) {
                strip.scrollLeft = Math.max(0, card.offsetLeft - strip.offsetLeft);
              }
            }
          }
        });
        const mats = (params.get('mats') || '').split(',');
        this.sides.forEach((side) => {
          if (this.state[side] && mats.includes(this.sideParam[side])) this.state.mats[side] = true;
        });
        // acc= tokens are "variantId" (one of it) or "variantId:qty"; render() paints the steppers.
        (params.get('acc') || '').split(',').forEach((token) => {
          if (!token) return;
          const [id, q] = token.split(':');
          if (!this.querySelector(`[data-kc-acc="${CSS.escape(id)}"]`)) return;
          const qty = parseInt(q, 10);
          this.state.accessories[id] = isNaN(qty) || qty < 1 ? 1 : Math.min(qty, 99);
        });
      }

      writeDeepLink() {
        if (!window.history || !window.history.replaceState) return;
        const url = new URL(window.location.href);
        this.sides.forEach((side) => {
          const p = this.sideParam[side];
          if (this.state[side]) url.searchParams.set(p, this.state[side]);
          else url.searchParams.delete(p);
        });
        const mats = this.sides.filter((s) => this.state[s] && this.state.mats[s]).map((s) => this.sideParam[s]);
        if (mats.length) url.searchParams.set('mats', mats.join(','));
        else url.searchParams.delete('mats');
        const accTokens = Object.keys(this.state.accessories).map((id) => {
          const qty = this.state.accessories[id];
          return qty > 1 ? `${id}:${qty}` : id;
        });
        if (accTokens.length) url.searchParams.set('acc', accTokens.join(','));
        else url.searchParams.delete('acc');
        window.history.replaceState(window.history.state || {}, '', url.toString());
      }

      /* ── Render ───────────────────────────────────────────────────── */

      selectedUnit(side) {
        return this.state[side] ? this.units[this.state[side]] : null;
      }

      selectedAccessories() {
        return Object.keys(this.state.accessories)
          .map((id) => {
            const card = this.querySelector(`[data-kc-acc="${CSS.escape(id)}"]`);
            if (!card) return null;
            return {
              id,
              qty: this.state.accessories[id] || 0,
              title: card.dataset.kcAccTitle || 'Accessory',
              cents: parseInt(card.dataset.kcAccPrice, 10) || 0,
            };
          })
          .filter((acc) => acc && acc.qty > 0);
      }

      matFor(side) {
        const unit = this.selectedUnit(side);
        return unit && unit.spec && unit.spec.mat_pack ? unit.spec.mat_pack : null;
      }

      render() {
        // Both totals are SUMMED from the same per-line figures the summary prints, never
        // derived from each other at the end (25 Aug 2026; direction flipped 30 Aug 2026).
        // Ex VAT is now the stored figure, so it adds up exactly; inc VAT rounds per line, so
        // summing it here is what keeps the total equal to the lines a customer adds up rather
        // than up to a penny away from them.
        let totalInc = 0;
        let totalEx = 0;
        let weight = 0;
        // Most units have no unit_weight_kg yet (David fills them as a batch — see the
        // backlog). A partial sum labelled "combined" would state a wrong fact, so the
        // line only shows when every SELECTED unit has a weight; an empty side is fine.
        let weightKnown = true;
        const lines = [];

        this.sides.forEach((side) => {
          const unit = this.selectedUnit(side);
          const mat = this.matFor(side);
          if (!mat || !unit) this.state.mats[side] = false;

          // Option rows
          this.querySelectorAll(`[data-kc-side="${side}"] [data-kc-unit]`).forEach((opt) => {
            const on = !!unit && opt.dataset.kcUnit === unit.key;
            opt.classList.toggle('is-selected', on);
            const input = opt.querySelector('[data-kc-input]');
            if (input && input.checked !== on) input.checked = on;
          });

          // Step heading number: orange once a unit is chosen; outlined when the side is left empty on purpose
          const step = this.querySelector('[data-kc-side="' + side + '"]');
          if (step) {
            step.classList.toggle('is-chosen', !!unit);
            step.classList.toggle('is-empty-chosen', !unit && !!this.emptyChosen[side]);
          }

          // Plan view slot
          const slot = this.querySelector(`.kc-slot[data-slot="${side}"]`);
          if (slot) {
            slot.dataset.type = unit ? unit.kit_type : '';
            slot.dataset.mat = this.state.mats[side] ? 'true' : 'false';
          }

          // Mat tick-box: one per unit card and all of them on show, each priced by
          // hydrateOptions from its own pack. Only the CHOSEN unit's can be ticked, so switching
          // units leaves every other box clear rather than carrying a tick across.
          this.querySelectorAll(`[data-kc-mat="${side}"]`).forEach((matEl) => {
            const matInput = matEl.querySelector('[data-kc-mat-input]');
            const mine = !!unit && !!mat && matEl.dataset.kcMatUnit === unit.key;
            const on = mine && !!this.state.mats[side];
            if (matInput) matInput.checked = on;
            matEl.classList.toggle('is-on', on);
          });

          // Summary lines
          if (unit) {
            lines.push(this.line(unit.title, unit.price));
            totalEx += unit.price;
            totalInc += this.incVat(unit.price);
            if (unit.spec && unit.spec.unit_weight_kg) weight += unit.spec.unit_weight_kg;
            else weightKnown = false;
            if (this.state.mats[side] && mat) {
              lines.push(this.line(`+ Rubber mat pack (${side})`, mat.price || 0, true));
              totalEx += mat.price || 0;
              totalInc += this.incVat(mat.price || 0);
            }
          } else {
            lines.push(
              this.emptyChosen[side]
                ? `<div class="kit-configurator__line kit-configurator__line--empty">${side.charAt(0).toUpperCase() + side.slice(1)} left empty</div>`
                : `<div class="kit-configurator__line kit-configurator__line--empty">No ${side} selected</div>`
            );
          }

          // Empty-side row: selected only when the customer chose it, never by default.
          const emptyRow = this.querySelector(`[data-kc-empty="${side}"]`);
          if (emptyRow) {
            const on = !unit && !!this.emptyChosen[side];
            emptyRow.classList.toggle('is-selected', on);
            const emptyInput = emptyRow.querySelector('[data-kc-empty-input]');
            if (emptyInput && emptyInput.checked !== on) emptyInput.checked = on;
          }
        });

        // Optional extras (step 3): any number of each, the stock stepper owns the count.
        const accessories = this.selectedAccessories();
        this.querySelectorAll('[data-kc-acc]').forEach((card) => {
          const qty = this.state.accessories[card.dataset.kcAcc] || 0;
          card.classList.toggle('is-selected', qty > 0);
          const addBtn = card.querySelector('[data-kc-acc-add]');
          const stepper = card.querySelector('[data-kc-acc-stepper]');
          if (addBtn) addBtn.hidden = qty > 0;
          if (stepper) {
            stepper.hidden = qty === 0;
            const input = stepper.querySelector('[data-kc-acc-input]');
            if (input && parseInt(input.value, 10) !== qty) input.value = qty;
          }
        });
        const accStep = this.querySelector('[data-kc-acc-step]');
        if (accStep) accStep.classList.toggle('is-chosen', accessories.length > 0);
        accessories.forEach((acc) => {
          lines.push(this.line(acc.qty > 1 ? `${acc.qty} × ${acc.title}` : acc.title, acc.cents * acc.qty));
          totalEx += acc.cents * acc.qty;
          totalInc += this.incVat(acc.cents * acc.qty);
        });

        const linesEl = this.querySelector('[data-kc-lines]');
        if (linesEl) linesEl.innerHTML = lines.join('');

        const weightEl = this.querySelector('[data-kc-weight]');
        if (weightEl) weightEl.textContent = weight && weightKnown ? `${Math.round(weight * 10) / 10} kg` : '—';

        const incEl = this.querySelector('[data-kc-total-inc]');
        const exEl = this.querySelector('[data-kc-total-ex]');
        if (incEl) incEl.textContent = this.formatMoney(totalInc);
        if (exEl) exEl.textContent = this.formatMoney(totalEx);

        // One side is a valid kit (17 Aug 2026): any unit chosen is enough to add to cart.
        const ready = !!(this.state.nearside || this.state.offside);
        const add = this.querySelector('[data-kc-add]');
        const addLabel = this.querySelector('[data-kc-add-label]');
        if (add) {
          add.disabled = !ready;
          add.setAttribute('aria-disabled', String(!ready));
        }
        if (addLabel) addLabel.textContent = ready ? 'Add to cart' : 'Choose a unit to add to cart';

        // Share and quote are useful as soon as there is a unit in the build.
        const anything = !!(this.state.nearside || this.state.offside);
        [this.querySelector('[data-kc-share]'), this.querySelector('[data-kc-quote]')].forEach((btn) => {
          if (!btn) return;
          btn.disabled = !anything;
          btn.setAttribute('aria-disabled', String(!anything));
        });

        this.renderVizBrief();
        this.setStatus('');
        this.writeDeepLink();
      }

      line(label, cents, sub = false) {
        return `
          <div class="kit-configurator__line${sub ? ' kit-configurator__line--sub' : ''}">
            <span>${this.escape(label)}</span>
            <span class="kit-configurator__line-amounts">
              ${this.formatMoney(cents)} ex. VAT
              <span class="kit-configurator__line-inc">${this.formatMoney(this.incVat(cents))} inc. VAT</span>
            </span>
          </div>`;
      }

      escape(str) {
        return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      }

      setStatus(message, isError = false) {
        const el = this.querySelector('[data-kc-status]');
        if (!el) return;
        el.hidden = !message;
        el.textContent = message || '';
        el.classList.toggle('is-error', !!isError);
      }

      /* ── Collapsible plan view (mobile) ───────────────────────────── */

      initVizCollapse() {
        this.viz = this.querySelector('[data-kc-viz]');
        this.vizWrap = this.querySelector('[data-kc-viz-wrap]');
        this.vizToggle = this.querySelector('[data-kc-viz-toggle]');
        const sentinel = this.querySelector('[data-kc-viz-sentinel]');
        if (!this.viz || !this.vizToggle) return;

        this.vizMobile = window.matchMedia('(max-width: 989px)');
        this.vizScrolledPast = false;
        this.vizManualOpen = false;

        // Pin the sticky wrapper to the panel's EXPANDED height on mobile, so collapsing the
        // panel never changes the page's layout (that reflow is what made it bounce).
        this.pinVizWrapHeight();
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.pinVizWrapHeight(), 150);
        });

        // Keep the bar just below the theme's sticky header when that slides back in on
        // scroll-up (Dawn's header is position: sticky, on-scroll-up).
        this.themeHeader = document.querySelector('.section-header');
        if (this.themeHeader && this.vizWrap) {
          let ticking = false;
          const sync = () => {
            ticking = false;
            if (!this.vizMobile.matches) { this.vizWrap.style.removeProperty('--kc-viz-top'); return; }
            const bottom = Math.max(0, this.themeHeader.getBoundingClientRect().bottom);
            this.vizWrap.style.setProperty('--kc-viz-top', `${Math.round(bottom) + 8}px`);
          };
          const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(sync); } };
          window.addEventListener('scroll', onScroll, { passive: true });
          this.themeHeader.addEventListener('transitionend', sync);
          sync();
        }

        this.vizToggle.addEventListener('click', () => {
          if (!this.vizMobile.matches) return;
          if (this.viz.classList.contains('is-collapsed')) {
            this.vizManualOpen = true;
            this.setVizCollapsed(false);
          } else {
            this.vizManualOpen = false;
            this.setVizCollapsed(true);
          }
        });

        if (sentinel && 'IntersectionObserver' in window) {
          this.vizObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              // "Scrolled past" = the sentinel has gone off the TOP, not merely off the bottom.
              this.vizScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              if (!this.vizMobile.matches) return;
              if (this.vizScrolledPast) {
                if (!this.vizManualOpen) this.setVizCollapsed(true);
              } else {
                this.vizManualOpen = false;
                this.setVizCollapsed(false);
              }
            },
            { threshold: 0 }
          );
          this.vizObserver.observe(sentinel);
        }

        const onMedia = () => {
          this.pinVizWrapHeight();
          if (!this.vizMobile.matches) this.setVizCollapsed(false);
          else if (this.vizScrolledPast && !this.vizManualOpen) this.setVizCollapsed(true);
        };
        if (this.vizMobile.addEventListener) this.vizMobile.addEventListener('change', onMedia);
        else this.vizMobile.addListener(onMedia);
      }

      setVizCollapsed(collapsed) {
        if (!this.viz) return;
        this.viz.classList.toggle('is-collapsed', collapsed);
        this.vizToggle.setAttribute('aria-expanded', String(!collapsed));
      }

      pinVizWrapHeight() {
        if (!this.vizWrap || !this.viz) return;
        if (!this.vizMobile.matches) { this.vizWrap.style.height = ''; return; }
        // Measure the panel expanded, then restore whatever state it was in.
        const wasCollapsed = this.viz.classList.contains('is-collapsed');
        if (wasCollapsed) this.viz.classList.remove('is-collapsed');
        const h = this.viz.getBoundingClientRect().height;
        if (wasCollapsed) this.viz.classList.add('is-collapsed');
        if (h > 0) this.vizWrap.style.height = `${Math.ceil(h)}px`;
      }

      renderVizBrief() {
        const brief = this.querySelector('[data-kc-viz-brief]');
        if (!brief) return;
        const label = { shelving: 'Shelving', workshop: 'Workshop', workstation: 'Workstation', eurobin: 'Eurobin', service: 'Service', dropfront: 'Drop Front', locksmith: 'Auto Locksmith' };
        let total = 0;
        let totalEx = 0; // summed per line, not derived from total - see render()
        const parts = this.sides.map((side) => {
          const unit = this.selectedUnit(side);
          const short = side === 'nearside' ? 'NS' : 'OS';
          if (!unit) return `<span><i class="kc-sw kc-sw--empty"></i>${short} empty</span>`;
          total += this.incVat(unit.price);
          totalEx += unit.price;
          if (this.state.mats[side]) {
            const mat = this.matFor(side);
            if (mat) {
              total += this.incVat(mat.price || 0);
              totalEx += mat.price || 0;
            }
          }
          // Swatch is orange-for-filled since 21 Aug 2026; the type name still says what it is.
          return `<span><i class="kc-sw kc-sw--on"></i>${short} ${this.escape(label[unit.kit_type] || unit.kit_type)}</span>`;
        });
        const accs = this.selectedAccessories();
        let accCount = 0;
        accs.forEach((a) => {
          total += this.incVat(a.cents * a.qty);
          totalEx += a.cents * a.qty;
          accCount += a.qty;
        });
        if (accCount) parts.push(`<span><i class="kc-sw kc-sw--on"></i>${accCount} extra${accCount === 1 ? '' : 's'}</span>`);
        const sep = '<span class="kc-brief__sep">·</span>';
        brief.innerHTML = parts.join(sep) + (total ? `${sep}<b>${this.formatMoney(totalEx)}</b> ex. VAT` : '');
      }

      /* ── Lightbox ─────────────────────────────────────────────────── */

      initLightbox() {
        this.lb = this.querySelector('[data-kc-lightbox]');
        if (!this.lb) return;
        this.lbImg = this.lb.querySelector('[data-kc-lb-img]');
        this.lbCaption = this.lb.querySelector('[data-kc-lb-caption]');
        this.lbCount = this.lb.querySelector('[data-kc-lb-count]');
        this.lbPrev = this.lb.querySelector('[data-kc-lb-prev]');
        this.lbNext = this.lb.querySelector('[data-kc-lb-next]');
        this.lbItems = [];
        this.lbIndex = 0;
        this.lbOpen = false;
        this.lbReturnFocus = null;

        // Open from any gallery thumbnail; the set is that thumbnail's own gallery strip.
        this.addEventListener('click', (evt) => {
          const item = evt.target.closest('[data-kc-gallery-item]');
          if (!item || !this.contains(item)) return;
          evt.preventDefault();
          const strip = item.closest('.kit-configurator__gallery');
          const all = strip ? Array.from(strip.querySelectorAll('[data-kc-gallery-item]')) : [item];
          const items = all.map((el) => ({ full: el.dataset.full, alt: el.dataset.alt || '' }));
          this.openLightbox(items, all.indexOf(item), item);
        });

        this.lb.querySelectorAll('[data-kc-lb-close]').forEach((el) => el.addEventListener('click', () => this.closeLightbox()));
        this.lbPrev.addEventListener('click', () => this.stepLightbox(-1));
        this.lbNext.addEventListener('click', () => this.stepLightbox(1));

        this.lbKeydown = (evt) => {
          if (!this.lbOpen) return;
          if (evt.key === 'Escape') { evt.preventDefault(); this.closeLightbox(); }
          else if (evt.key === 'ArrowLeft') { evt.preventDefault(); this.stepLightbox(-1); }
          else if (evt.key === 'ArrowRight') { evt.preventDefault(); this.stepLightbox(1); }
        };

        // Swipe left/right on the image; a mostly-vertical drag is left to the browser.
        const stage = this.lb.querySelector('[data-kc-lb-stage]');
        let startX = 0, startY = 0, tracking = false;
        stage.addEventListener('touchstart', (evt) => {
          if (evt.touches.length !== 1) return;
          startX = evt.touches[0].clientX; startY = evt.touches[0].clientY; tracking = true;
        }, { passive: true });
        stage.addEventListener('touchend', (evt) => {
          if (!tracking) return;
          tracking = false;
          const t = evt.changedTouches[0];
          const dx = t.clientX - startX, dy = t.clientY - startY;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepLightbox(dx < 0 ? 1 : -1);
        }, { passive: true });

        // Back button closes the viewer instead of leaving the configurator.
        window.addEventListener('popstate', () => {
          if (this.lbOpen) this.closeLightbox(true);
        });
      }

      openLightbox(items, index, returnFocus) {
        if (!this.lb || !items.length) return;
        this.lbItems = items;
        this.lbIndex = Math.max(0, Math.min(index, items.length - 1));
        this.lbReturnFocus = returnFocus || document.activeElement;
        this.lbOpen = true;
        this.lb.hidden = false;
        document.documentElement.classList.add('kc-lightbox-open');
        document.addEventListener('keydown', this.lbKeydown);
        const single = items.length < 2;
        this.lbPrev.hidden = single;
        this.lbNext.hidden = single;
        this.showLightboxIndex();
        try { window.history.pushState({ kcLightbox: true }, '', window.location.href); } catch (e) { /* ignore */ }
        const close = this.lb.querySelector('[data-kc-lb-close].kit-configurator__lightbox-close');
        if (close) close.focus();
      }

      showLightboxIndex() {
        const item = this.lbItems[this.lbIndex];
        if (!item) return;
        this.lbImg.src = item.full;
        this.lbImg.alt = item.alt || '';
        if (this.lbCaption) this.lbCaption.textContent = item.alt || '';
        if (this.lbCount) this.lbCount.textContent = `${this.lbIndex + 1} / ${this.lbItems.length}`;
        // Warm the neighbours so swiping feels instant.
        [this.lbIndex - 1, this.lbIndex + 1].forEach((i) => {
          const n = this.lbItems[(i + this.lbItems.length) % this.lbItems.length];
          if (n && n.full !== item.full) { const img = new Image(); img.src = n.full; }
        });
      }

      stepLightbox(delta) {
        if (!this.lbOpen || this.lbItems.length < 2) return;
        this.lbIndex = (this.lbIndex + delta + this.lbItems.length) % this.lbItems.length;
        this.showLightboxIndex();
      }

      closeLightbox(fromHistory = false) {
        if (!this.lbOpen) return;
        this.lbOpen = false;
        this.lb.hidden = true;
        this.lbImg.removeAttribute('src');
        document.documentElement.classList.remove('kc-lightbox-open');
        document.removeEventListener('keydown', this.lbKeydown);
        if (!fromHistory && window.history.state && window.history.state.kcLightbox) {
          window.history.back();
        }
        if (this.lbReturnFocus && typeof this.lbReturnFocus.focus === 'function') this.lbReturnFocus.focus();
        this.lbReturnFocus = null;
      }

      /* ── Share ────────────────────────────────────────────────────── */

      shareBuild() {
        const url = window.location.href; // render() keeps ?ns=&os=&mats= current
        const title = document.title;
        const van = (this.dataset.van || '').trim();
        const text = van ? `My van racking build for the ${van}` : 'My van racking build';
        const label = this.querySelector('[data-kc-share-label]');
        const flash = (msg) => {
          if (!label) return;
          const original = 'Share this build';
          label.textContent = msg;
          setTimeout(() => (label.textContent = original), 2200);
        };

        // Native share sheet on phones (WhatsApp, Messages, Mail…); copy-link elsewhere.
        if (navigator.share) {
          navigator.share({ title, text, url }).catch((e) => {
            if (e && e.name !== 'AbortError') this.copyLink(url, flash);
          });
          return;
        }
        this.copyLink(url, flash);
      }

      copyLink(url, flash) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(
            () => flash('Link copied'),
            () => window.prompt('Copy this link', url)
          );
        } else {
          window.prompt('Copy this link', url);
        }
      }

      /* ── Quote ────────────────────────────────────────────────────── */

      quoteBuild() {
        const btn = this.querySelector('[data-kc-quote]');
        if (!btn || btn.disabled) return;
        const lines = [];
        let total = 0;
        let totalEx = 0; // summed per line, not derived from total - see render()
        this.sides.forEach((side) => {
          const unit = this.selectedUnit(side);
          if (!unit) return;
          lines.push(`${unit.title}${unit.sku ? ` (${unit.sku})` : ''} - ${this.formatMoney(unit.price)}`);
          total += this.incVat(unit.price);
          totalEx += unit.price;
          if (this.state.mats[side]) {
            const mat = this.matFor(side);
            if (mat) {
              lines.push(`Rubber mat pack (${side}) - ${this.formatMoney(mat.price || 0)}`);
              total += this.incVat(mat.price || 0);
              totalEx += mat.price || 0;
            }
          }
        });
        this.selectedAccessories().forEach((acc) => {
          lines.push(`${acc.qty > 1 ? `${acc.qty} x ` : ''}${acc.title} - ${this.formatMoney(acc.cents * acc.qty)}`);
          total += this.incVat(acc.cents * acc.qty);
          totalEx += acc.cents * acc.qty;
        });
        const message =
          'Hi StowPro, here is the build I have put together so far:\n' +
          `Van: ${this.dataset.van || ''}\n\n` +
          lines.map((l) => `- ${l}`).join('\n') +
          `\n\nTotal ${this.formatMoney(totalEx)} ex VAT (${this.formatMoney(total)} inc VAT)\n` +
          `Build link: ${window.location.href}\n\n` + // render() keeps ?ns=&os=&mats=&acc= current
          'What I would like to change: ';

        // WhatsApp when the section has a number; otherwise email. The customer's own app
        // sends it, so there is no form, no backend and nothing stored here.
        const whatsapp = (btn.dataset.quoteWhatsapp || '').replace(/\D/g, '');
        if (whatsapp) {
          window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
          return;
        }
        const email = btn.dataset.quoteEmail || '';
        if (!email) return;
        const subject = `My van racking build - ${this.dataset.van || 'changes needed'}`;
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      }

      /* ── Cart ─────────────────────────────────────────────────────── */

      buildKey() {
        return this.sides
          .map((side) => {
            const unit = this.selectedUnit(side);
            return unit ? unit.key + (this.state.mats[side] ? '+mat' : '') : '';
          })
          .filter(Boolean)
          .join('+');
      }

      addToCart() {
        const add = this.querySelector('[data-kc-add]');
        if (!add || add.disabled) return;

        const properties = {
          _van: this.dataset.van || '',
          _source: 'configurator',
          _build: this.buildKey(),
        };

        const items = [];
        const skipped = [];
        this.sides.forEach((side) => {
          const unit = this.selectedUnit(side);
          if (!unit) return;
          items.push({ id: unit.variant_id, quantity: 1, properties });
          if (this.state.mats[side]) {
            const mat = this.matFor(side);
            if (mat && mat.variant_id) items.push({ id: mat.variant_id, quantity: 1, properties });
            else skipped.push(`${side} mat pack`);
          }
        });
        this.selectedAccessories().forEach((acc) => {
          items.push({ id: parseInt(acc.id, 10), quantity: acc.qty, properties });
        });
        if (!items.length) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        add.classList.add('loading');
        add.setAttribute('aria-disabled', 'true');
        const spinner = add.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        this.setStatus('');

        fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setStatus(response.description || response.message || 'Could not add to cart.', true);
              if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
                publish(PUB_SUB_EVENTS.cartError, { source: 'kit-configurator', errors: response.errors || response.description, message: response.message });
              }
              return;
            }

            const count = (response.items || []).length || items.length;
            let note = `Added ${count} item${count === 1 ? '' : 's'} to your basket.`;
            if (skipped.length) note += ` (${skipped.join(' and ')} not added: no product exists for it yet.)`;
            this.setStatus(note);

            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kit-configurator', cartData: response });
            }
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(response);
            } else {
              window.location = this.dataset.cartUrl || '/cart';
            }
          })
          .catch((e) => {
            console.error(e);
            this.setStatus('Something went wrong adding to cart. Please try again.', true);
          })
          .finally(() => {
            add.classList.remove('loading');
            add.setAttribute('aria-disabled', 'false');
            if (spinner) spinner.classList.add('hidden');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }
    }
  );
}
